import { useEffect, useState } from 'react';
import { useRenderStats } from '@/dev/stats';
import { useHeroData } from '@/hero/data';
import { textureBytes } from '@/render/materials';
import { useSelectionStore } from '@/state/selection';
import { hero } from '@/state/tour';

const fmtBytes = (b: number) =>
  b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`;

export function LayersPanel() {
  const hidden = useSelectionStore((s) => s.hidden);
  const toggle = useSelectionStore((s) => s.toggleHidden);
  const data = useHeroData((s) => s.data);
  const calls = useRenderStats((s) => s.calls);
  const [textures, setTextures] = useState<number | null>(null);
  const totalBytes = Object.values(data?.stats ?? {}).reduce((a, s) => a + s.bytes, 0);
  const presets = new Set(
    Object.values(data?.stats ?? {}).flatMap((s) =>
      Object.entries(s.presets ?? {})
        .filter(([, n]) => n > 0)
        .map(([id]) => id),
    ),
  );

  useEffect(() => {
    let live = true;
    void textureBytes().then((b) => live && setTextures(b));
    return () => {
      live = false;
    };
  }, []);

  return (
    <div className="lens-panel layers">
      <div className="lens-bar">
        <span className="lens-dot" />
        <span>IFC files · {hero.layers.length} layers</span>
        <span className="lens-right">{totalBytes ? fmtBytes(totalBytes) : ''}</span>
      </div>
      <ul className="layer-list">
        {hero.layers.map((l) => {
          const st = data?.stats[l.id];
          const off = hidden.includes(l.id);
          return (
            <li key={l.id} data-off={off}>
              <label>
                <input type="checkbox" checked={!off} onChange={() => toggle(l.id)} />
                <span className="swatch" style={{ background: l.tint ?? '#8b8f98' }} />
                <span className="layer-name">{l.label ?? l.discipline}</span>
                <span className="layer-meta">
                  {st
                    ? `${st.elements} el · ${st.triangles.toLocaleString()} tri · ${fmtBytes(st.bytes)}`
                    : l.glb}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      <dl className="lens-stats">
        <div>
          <dt>textures</dt>
          <dd>{textures ? fmtBytes(textures) : '–'}</dd>
        </div>
        <div>
          <dt>presets</dt>
          <dd>{presets.size || '–'}</dd>
        </div>
        <div>
          <dt>draw calls</dt>
          <dd>{calls || '–'}</dd>
        </div>
      </dl>
      <p className="lens-foot">
        Edges first, straight from the IFC geometry.
        <br />
        Then the flat colours the file carries.
        <br />
        Then materials from the IFC material names, rendered here in your browser. No server.
      </p>
    </div>
  );
}
